'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

// FAQ data structure
type FAQreferral = {
  question: string
  answer: string
}

// Photovoltaik FAQ data
const photovoltaikFAQs: FAQreferral[] = [
  {
    question: 'Was ist das Empfehlungsprogramm für Solaranlagen?',
    answer:
      ' Wenn Sie uns einen Kunden empfehlen, der eine Photovoltaikanlage durch GoDeutschland installieren lässt, erhalten Sie eine Prämie in Höhe von 200 € ausgezahlt.',
  },
  {
    question: 'Was bietet das Empfehlungsprogramm für Isolierungen?',
    answer:
      'Wenn Sie uns einen Kunden empfehlen, der eine Isolierung durch GoDeutschland installieren lässt, erhalten Sie eine Prämie in Höhe von 300 € ausgezahlt. ',
  },
  {
    question: 'Warum gibt es das GoDeutschland Empfehlungsprogramm?',
    answer:
      'Das kontinuierliche Anwerben von Neukunden ist teurer Prozess, der letzten endlich zu einem höheren Endkundenpreis führen kann. Wir möchten Energielösungen anbieten, die sich jeder leisten kann. Unsere Mission ist die Volks-Energielösung! Empfehle GoDeutschland jedem in deiner Umgebung und sorge so dafür, dass jeder Zugang zu günstigen Energielösungen bekommt, so können wir langfristig die günstigen Preise anbieten. ',
  },
  {
    question: 'Gibt es ein Empfehlungsprogramm innerhalb des GoDeutschland Kundenportal?',
    answer:
      'Ja, das GoDeutschland Empfehlungsprogramm gibt es auch im Kundenportal unter Quick Links. ',
  },
  {
    question: 'Wann wird meine Empfehlungsprämie ausgezahlt?',
    answer:
      'Dies Auszahlung erfolgt innerhalb von 4-8 Wochen nachdem die Zahlung der empfohlenen Person bei uns eingegangen ist und die Installation vollständig erfolgt ist. ',
  },
  {
    question: 'Kann ich GoDeutschland auch weiterempfehlen ohne Kunde zu sein?',
    answer:
      'Natürlich können Sie GoDeutschland auch weiterempfehlen, wenn Sie nicht Kunde bei bei GoDeutschland sind. ',
  },
]

// Accordion Item Component
const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string
  answer: string
  isOpen: boolean
  onClick: () => void
}) => {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 dark:border-gray-700 dark:bg-gray-800/40">
      <button
        className="flex w-full cursor-pointer items-center justify-between px-6 py-5 text-left text-base font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:text-white dark:hover:bg-gray-800/70"
        onClick={onClick}
      >
        <span className="text-lg">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="ml-4 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-500 dark:bg-orange-900/30 dark:text-orange-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div
              className="px-6 pt-0 pb-5 text-base text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: answer }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Main FAQ Section Component
export default function PhotovoltaikFAQ() {
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null)

  // Intersection observer for section entry animation
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  })

  // Handle accordion toggle
  const toggleAccordion = (index: number) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index)
  }

  return (
    <section ref={sectionRef} className="relative py-24 md:px-4 lg:px-0">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-2 inline-block text-lg font-medium text-orange-500"
          >
            <span className="inline-block rounded-full bg-orange-100 px-3 py-1 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
              Häufig gestellte Fragen
            </span>
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6 text-3xl font-bold sm:text-4xl lg:text-5xl"
          >
            Haben Sie Fragen zu Empfehlungsprogramm?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300"
          >
            Hier finden Sie Antworten auf die häufigsten Fragen zu unseren Empfehlungsprogramm
          </motion.p>
        </motion.div>

        {/* Accordion Items */}
        <div className="mx-auto max-w-7xl">
          {photovoltaikFAQs.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <AccordionItem
                question={item.question}
                answer={item.answer}
                isOpen={openAccordionIndex === index}
                onClick={() => toggleAccordion(index)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
